import { useEffect, useRef, useState, type FormEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDocFromServer } from 'firebase/firestore';
import { EmptyState } from '../components/EmptyState';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/Page';
import { Switch } from '../components/ui/Switch';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../auth/AuthProvider';
import { db, functions } from '../lib/firebase';
import { canManageCompany, type Company } from '../types/models';
import { ReadFailure } from '../components/ReadFailure';
import { withReadTimeout } from '../lib/readLoading';

export default function CompanySettingsPage() {
  usePageTitle('Company settings');
  const { activeCompanyId, activeCompanyName, activeRole, user } = useAuth();
  const scope = JSON.stringify([user?.uid, activeCompanyId, activeRole]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const [accountsQueueEnabled, setAccountsQueueEnabled] = useState(true);
  const [contactEmailEnabled, setContactEmailEnabled] = useState(false);
  const [additionalChargesEnabled, setAdditionalChargesEnabled] = useState(true);
  const [baseline, setBaseline] = useState<Record<string, boolean>>({});
  const [loadedScope, setLoadedScope] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [readAttempt, setReadAttempt] = useState(0);
  const [readError, setReadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const canManage = canManageCompany(activeRole ?? undefined);

  useEffect(() => {
    setLoaded(false);
    setLoadedScope(null);
    setSaving(false);
    setNotice(null);
    if (!activeCompanyId || !canManage) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setReadError(null);
    void withReadTimeout(getDocFromServer(doc(db, 'companies', activeCompanyId)))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) throw new Error('Company settings not found.');
        const company = snap.exists()
          ? ({ id: snap.id, ...snap.data() } as Company)
          : null;
        setAccountsQueueEnabled(
          company?.features?.accountsQueueEnabled !== false
        );
        setContactEmailEnabled(company?.features?.contactEmailEnabled === true);
        setAdditionalChargesEnabled(company?.features?.additionalChargesEnabled !== false);
        setBaseline({accountsQueueEnabled: company?.features?.accountsQueueEnabled !== false,
          contactEmailEnabled: company?.features?.contactEmailEnabled === true,
          additionalChargesEnabled: company?.features?.additionalChargesEnabled !== false});
        setLoadedScope(scope);
        setLoaded(true);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) setReadError(e instanceof Error ? e.message : 'Could not load company settings.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scope, canManage, readAttempt]);

  const ready = loaded && loadedScope === scope;

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!activeCompanyId || !canManage || !ready || saving) return;

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saveFeatures = httpsCallable<
        { companyId: string; accountsQueueEnabled?: boolean; contactEmailEnabled?: boolean; additionalChargesEnabled?: boolean },
        { success: boolean }
      >(functions, 'saveCompanyFeatures');
      const values = {accountsQueueEnabled, contactEmailEnabled, additionalChargesEnabled};
      const changes = Object.fromEntries(Object.entries(values).filter(([key, value]) => baseline[key] !== value));
      if (Object.keys(changes).length) await saveFeatures({ companyId: activeCompanyId, ...changes });
      if (currentScope.current === scope) { setBaseline(values); setNotice('Company settings saved.'); }
    } catch (e) {
      console.error(e);
      if (currentScope.current === scope) setError((e as Error).message || 'Could not save company settings.');
    } finally {
      if (currentScope.current === scope) setSaving(false);
    }
  }

  if (!canManage) {
    return (
      <EmptyState
        title="Settings are admin-managed"
        description="This matches the iOS app: only company admins can change workflow settings."
      />
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Company settings" description={activeCompanyName ?? undefined} />

      <div className="mt-5 space-y-2.5 empty:hidden">
        {error && <Alert tone="error">{error}</Alert>}
        {notice && <Alert tone="success">{notice}</Alert>}
        {readError && <ReadFailure message={readError} retry={() => setReadAttempt((n) => n + 1)} />}
      </div>

      <form onSubmit={save}>
        <h2 className="eyebrow ml-1 mt-6">Workflow</h2>
        {/* This setting changes what a whole company sees, so the card states both
            halves as full sentences before anyone touches the switch. */}
        <section className="mt-2.5 overflow-hidden rounded-[14px] border border-hair bg-elev">
          <div className="flex items-start justify-between gap-4 px-5 py-4">
            <div>
              <h3 className="text-[16.5px] font-semibold text-ink">Accounts queue</h3>
              <p className="mt-0.5 text-[13.5px] text-ink3">Who prices and invoices completed work.</p>
            </div>
            <Switch
              label="Accounts queue"
              checked={accountsQueueEnabled}
              disabled={!ready || loading || saving}
              onChange={setAccountsQueueEnabled}
            />
          </div>
          <div className="border-t border-rowhair px-5 py-4 text-[14.5px] leading-relaxed text-ink2">
            <p><span className="font-semibold text-ink">On:</span> a completed job appears in the Accounts queue until it is marked invoiced, and pricing is available to admins and accounts users.</p>
            <p className="mt-3 border-t border-rowhair pt-3">
              <span className="font-semibold text-ink">Off:</span> completed jobs go straight to the archive, the queue is hidden, and pricing and invoicing are disabled. Nothing already priced is lost.
            </p>
          </div>
        </section>

        <section className="mt-3 overflow-hidden rounded-[14px] border border-hair bg-elev">
          <div className="flex items-start justify-between gap-4 px-5 py-4">
            <div><h3 className="text-[16.5px] font-semibold text-ink">Contact email</h3>
              <p className="mt-0.5 text-[13.5px] text-ink3">Show optional email fields for collection and delivery contacts.</p></div>
            <Switch label="Contact email" checked={contactEmailEnabled} disabled={!ready || loading || saving} onChange={setContactEmailEnabled} />
          </div>
          <div className="flex items-start justify-between gap-4 border-t border-rowhair px-5 py-4">
            <div><h3 className="text-[16.5px] font-semibold text-ink">Additional charges</h3>
              <p className="mt-0.5 text-[13.5px] text-ink3">Allow charge flags and an optional price breakdown. Switching off keeps existing records.</p></div>
            <Switch label="Additional charges" checked={additionalChargesEnabled} disabled={!ready || loading || saving} onChange={setAdditionalChargesEnabled} />
          </div>
        </section>

        <h2 className="eyebrow ml-1 mt-7">Company</h2>
        <section className="mt-2.5 overflow-hidden rounded-[14px] border border-hair bg-elev px-5">
          <div className="flex min-h-12 flex-col gap-0.5 py-3 sm:flex-row sm:items-center sm:gap-3">
            <span className="text-[14.5px] text-ink2 sm:shrink-0">Company ID</span>
            <span className="min-w-0 flex-1 break-words font-mono text-[15px] text-ink sm:text-right">{activeCompanyId ?? '—'}</span>
          </div>
          <div className="flex min-h-12 flex-col gap-0.5 border-t border-rowhair py-3 sm:flex-row sm:items-center sm:gap-3">
            <span className="text-[14.5px] text-ink2 sm:shrink-0">Evidence retention</span>
            <span className="min-w-0 flex-1 break-words text-[15px] text-ink sm:text-right">For the life of the company</span>
          </div>
        </section>

        <Alert tone="info" className="mt-4">
          Changes apply to everyone in this company, and are recorded against your name.
        </Alert>

        <div className="mt-5 flex justify-end">
          <Button type="submit" size="lg" loading={saving} disabled={!ready || loading || saving}>
            {loading ? 'Loading…' : 'Save settings'}
          </Button>
        </div>
      </form>
    </div>
  );
}
